import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { useGetManagedCarePlansQuery } from "services/managed-care-plans";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";

type ManagedCarePlanFieldProps = {
  label: string;
  disabled?: boolean;
  required?: boolean;
  hideIcon?: boolean;
  shrinkWidth?: boolean;
  sx?: SxProps;
  onOptionSelected?: (value: any, field?: string) => void;
};

type RhfWizardSelectFieldProps<T extends FieldValues> =
  ManagedCarePlanFieldProps & UseControllerProps<T>;

export default function ManagedCarePlanField<T extends FieldValues>({
  control,
  name,
  disabled,
  label,
  hideIcon,
  required = false,
  shrinkWidth = true,
  sx,
  onOptionSelected
}: RhfWizardSelectFieldProps<T>) {
  const { data: managedCarePlans, isLoading } = useGetManagedCarePlansQuery({
    is_active: true
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: ManagedCarePlan) =>
      `${option.name} (${option.managed_care_provider.name})`
  });

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: shrinkWidth ? "calc(100% - 15px)" : undefined }}
    >
      {Boolean(!hideIcon) && <VolunteerActivismIcon />}

      <AutocompleteFormField
        options={managedCarePlans}
        name={name}
        label={label}
        disabled={Boolean(disabled)}
        required={required}
        isLoading={isLoading}
        getOptionLabel={(option: ManagedCarePlan) =>
          `${option.name} (${option.managed_care_provider.name})`
        }
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        popupIcon={<ArrowDropDownIcon color="action" />}
        control={control}
        filterOptions={filterOptions}
        sx={{ ml: 2, mt: hideIcon ? 1 : 2, ...sx }}
        onOptionSelected={onOptionSelected}
      />
    </Box>
  );
}
